import { useEffect, useMemo } from 'react'

import useConversationIdsBySection from 'conversations/hooks/useConversationIdsBySection'
import { conversationListCounts } from 'conversations/modules/conversation/api'
import {
  getCounts,
  getHasMoreSidebarConversations,
} from 'conversations/modules/conversation/selectors'
import { getCurrentInboxId } from 'conversations/modules/user/selectors'
import { Pill } from 'conversations/utils/pillUtil'
import { useDispatch, useSelector } from 'react-redux'
import { receiveCounts } from 'shared/store/actions/conversation'
import { Counts } from 'shared/typings/conversations/Counts'

import { RootState } from 'typesafe-actions'

export default function useCounts(
  pillTypes: Pill[],
  subscriptionId: string,
  sectionUUID?: string
): Counts {
  const uuid = sectionUUID ?? ''
  const dispatch = useDispatch()
  const inboxId = useSelector(getCurrentInboxId)

  useEffect(() => {
    const fetchCounts = async () => {
      const counts = await conversationListCounts(subscriptionId, inboxId)
      dispatch(receiveCounts({ [uuid]: counts }))
    }
    fetchCounts()
  }, [dispatch, uuid, subscriptionId, inboxId])

  const shouldUseServerOpenCounts = useSelector(
    (state: RootState) =>
      getHasMoreSidebarConversations(`${uuid}-open`)(state) === true ||
      getHasMoreSidebarConversations(`${uuid}-open`)(state) === undefined
  )
  const shouldUseServerClosedCounts = useSelector(
    (state: RootState) =>
      getHasMoreSidebarConversations(`${uuid}-closed`)(state) === true ||
      getHasMoreSidebarConversations(`${uuid}-closed`)(state) === undefined
  )
  const shouldUseServerAllCounts = useSelector(
    (state: RootState) =>
      getHasMoreSidebarConversations(`${uuid}-all`)(state) === true ||
      getHasMoreSidebarConversations(`${uuid}-all`)(state) === undefined
  )
  const openConversationCount = useConversationIdsBySection(uuid, 'open', inboxId).length
  const closedConversationCount = useConversationIdsBySection(uuid, 'closed', inboxId).length
  const allConversationCount = useConversationIdsBySection(uuid, 'all', inboxId).length
  const serverCounts = useSelector(getCounts(uuid))

  return useMemo(
    () => ({
      open: shouldUseServerOpenCounts ? serverCounts.open : openConversationCount,
      closed: shouldUseServerClosedCounts ? serverCounts.closed : closedConversationCount,
      all: shouldUseServerAllCounts ? serverCounts.all : allConversationCount,
    }),
    [
      shouldUseServerOpenCounts,
      shouldUseServerClosedCounts,
      shouldUseServerAllCounts,
      allConversationCount,
      closedConversationCount,
      openConversationCount,
      serverCounts,
    ]
  )
}
